// EvidenceChips — compact, at-a-glance justification for a match. Turns the
// engine's score_breakdown into a row of tinted chips (green = corroborating,
// amber = wants a look, gray = neutral fact) so Dawn can trust a suggestion
// without opening anything. Replaces the older plain-text "why" string.

import React from 'react';
import type { ScoreBreakdown } from './types';
import { Tag } from '../components/ui/Tag';

// score_breakdown is JSONB — normally an object, but tolerate a JSON string and
// bad/missing data. Returns null when there's nothing trustworthy to show.
export function parseBreakdown(raw: ScoreBreakdown | string | null | undefined): ScoreBreakdown | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as ScoreBreakdown; } catch { return null; }
  }
  return typeof raw === 'object' ? raw : null;
}

// MatchPicker still draws its card chip from these three.
export const CHIP = 'inline-flex items-center whitespace-nowrap rounded-full text-[10px] font-medium px-1.5 py-0.5 ring-1 ring-inset';
export const CHIP_GREEN = 'bg-green-500/15 text-green-300 ring-green-500/25';
export const CHIP_AMBER = 'bg-amber-500/15 text-amber-300 ring-amber-500/25';
export const CHIP_GRAY = 'bg-surface-container-highest text-on-surface-variant ring-outline-variant';

export default function EvidenceChips({
  breakdown, last4, className = '',
}: {
  breakdown: ScoreBreakdown | string | null | undefined;
  last4?: string | null;                 // actual charge last4, for the "card ····NNNN" chip
  className?: string;
}) {
  const b = parseBreakdown(breakdown);
  if (!b) return null;

  const chips: React.ReactNode[] = [];

  // Amount: exact match reassures; otherwise the implied tip explains the gap.
  if (b.amountExact) {
    chips.push(<Tag key="amt" size="sm" tone="good">exact amount</Tag>);
  } else if (b.impliedTip != null && b.impliedTip > 0) {
    const pct = b.impliedTipPct != null ? ` (${Math.round(b.impliedTipPct)}%)` : '';
    chips.push(<Tag key="tip" size="sm" tone="warn">tip ${b.impliedTip.toFixed(2)}{pct}</Tag>);
  }

  // Date proximity.
  if (b.dateDeltaDays != null) {
    chips.push(
      <Tag key="date" size="sm">
        {b.dateDeltaDays === 0 ? 'same day' : `±${Math.abs(b.dateDeltaDays)}d`}
      </Tag>,
    );
  }

  // Card: only assert when the read card actually matched the charge's card.
  if (b.last4 && last4) {
    chips.push(<Tag key="card" size="sm" tone="good">card ····{last4}</Tag>);
  }

  // Vendor-name similarity — green once it's a confident name match.
  if (b.vendorScore > 0) {
    chips.push(
      <Tag key="vendor" size="sm" tone={b.vendorScore >= 0.5 ? 'good' : 'neutral'}>
        vendor {Math.round(b.vendorScore * 100)}%
      </Tag>,
    );
  }

  // Ambiguity/contest is no longer a chip: the reconcile board states it as a plain
  // sentence under the paired row (see MatchPairGrid), so it reads once, clearly.

  if (chips.length === 0) return null;
  return <div className={`flex flex-wrap items-center gap-1 ${className}`}>{chips}</div>;
}
